import { Image as ImageIcon } from '@styled-icons/bootstrap/Image';
import { OpenInNew } from '@styled-icons/material';
import Image from 'next/legacy/image';
import { useEffect, useRef, useState } from 'react';
import { ASSET_TYPEDREAM_LINK } from '../../../constants';
import selectMediaStyles from '../SelectMedia.module.css';
import { gradientMaker } from './gradientMaker';
import { Collection, Illustration } from './illustrationsList';
import styles from './SelectMediaIllustration.module.css';

const getCollectionsAsync = async () => {
  const { collections } = await import('./illustrationsList');
  return Object.values(collections);
};

const getIllustrationsAsync = async (keyword?: string, collection?: string) => {
  const { illustrations } = await import('./illustrationsList');
  const values = Object.values(illustrations);

  if (keyword) {
    const lowercasedKeyword = keyword.toLowerCase();
    return values.filter(
      (i) => i.keywords.some((k) => k.includes(lowercasedKeyword))
        || i.category.some((c) => c.includes(lowercasedKeyword))
        || i.title.toLowerCase().includes(lowercasedKeyword),
    );
  }

  if (collection) {
    return values.filter((i) => i.collection.key === collection);
  }

  return values;
};

enum Steps {
  COLLECTIONS,
  ILLUSTRATIONS,
}

export interface SelectMediaIllustrationProps {
  onSelect: (url: string) => void
}

export function SelectMediaIllustration(props: SelectMediaIllustrationProps) {
  const { onSelect } = props;

  const [collections, setCollections] = useState<Array<Collection>>([]);
  const getCollections = (...args: Parameters<typeof getCollectionsAsync>) => {
    getCollectionsAsync(...args).then(setCollections);
  };
  useEffect(getCollections, []);

  const [illustrations, setIllustrations] = useState<Array<Illustration>>([]);
  const getIllustrations = (
    ...args: Parameters<typeof getIllustrationsAsync>
  ) => {
    getIllustrationsAsync(...args).then(setIllustrations);
  };

  useEffect(getIllustrations, []);

  const [step, setStep] = useState(Steps.COLLECTIONS);

  const formRef = useRef<HTMLFormElement>(null);

  return (
    <div>
      <div className={selectMediaStyles.filter}>
        <form ref={formRef}>
          <input
            className={selectMediaStyles.input}
            type="text"
            placeholder="Search illustrations..."
            onChange={(e) => {
              getIllustrations(e.target.value);
              setStep(Steps.ILLUSTRATIONS);
            }}
          />
        </form>
        <a
          href={ASSET_TYPEDREAM_LINK}
          target="_blank"
          rel="noreferrer"
          className={styles.exploreButtonLink}
        >
          <button type="button" className={styles.exploreButton}>
            <ImageIcon size={16} color="var(--accent)" />
            Explore Assets
            <OpenInNew size={16} />
          </button>
        </a>
      </div>

      <div
        className={styles.illustrationContent}
        style={{
          display: step === Steps.COLLECTIONS ? 'block' : 'none',
        }}
      >
        <div className="is-flex is-flex-direction-row is-align-items-center is-justify-content-space-between">
          <div className={styles.collectionsTitle}>Collections</div>
          <button
            type="button"
            className={styles.stepButton}
            onClick={() => {
              getIllustrations();
              setStep(Steps.ILLUSTRATIONS);
              formRef.current?.reset();
            }}
          >
            See All →
          </button>
        </div>
        <div className={styles.illustrationList}>
          {collections.map((i) => (
            <div
              key={i.key}
              className={styles.illustrationContainer}
              onClick={() => {
                getIllustrations(undefined, i.key);
                setStep(Steps.ILLUSTRATIONS);
              }}
              role="none"
            >
              <Image
                src={i.previewURL}
                className={styles.illustrationPreview}
                alt="preview"
                height={120}
                width={170}
                unoptimized
              />
              <div className={styles.illustrationSource}>
                {i.source.title}
              </div>
              <div className={styles.illustrationName}>{i.title}</div>
              <div className={styles.illustrationCount}>
                {i.count}
                {' '}
                illustrations
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        className={styles.illustrationContent}
        style={{
          display: step === Steps.ILLUSTRATIONS ? 'block' : 'none',
        }}
      >

        <button
          type="button"
          className={styles.backButton}
          onClick={() => {
            setStep(Steps.COLLECTIONS);
          }}
        >
          ← Back to Collections
        </button>

        <div className={styles.illustrationList}>
          <a
            href={gradientMaker.downloadURL}
            target="_new"
            className={styles.gradientMakerIllustration}
          >
            <div
              key={gradientMaker.key}
              className={styles.illustrationContainer}
            >
              <Image
                src={gradientMaker.previewURL}
                className={styles.illustrationPreview}
                alt="preview"
                height={120}
                width={250}
              />
              <div className={styles.illustrationSource}>
                {gradientMaker.source.title}
              </div>
              <div className={styles.illustrationName}>
                {gradientMaker.title}
              </div>
              <div className={styles.illustrationAction}>
                Download →
              </div>
            </div>
          </a>
          {illustrations.map((i) => (i.action === 'download' ? (
            <a key={i.key} href={`${i.downloadURL}`} target="_new">
              <div key={i.title} className={styles.illustrationContainer}>
                <Image
                  src={i.previewURL}
                  className={styles.illustrationPreview}
                  alt="preview"
                  height={120}
                  width={170}
                  unoptimized
                />
                <div className={styles.illustrationSource}>
                  {i.source.title}
                </div>
                <div className={styles.illustrationName}>{i.title}</div>
                <div className={styles.illustrationAction}>
                  Download →
                </div>
              </div>
            </a>
          ) : (
            <div
              key={i.key}
              className={styles.illustrationContainer}
              onClick={() => onSelect(i.useURL)}
              role="none"
            >
              <Image
                src={i.previewURL}
                className={styles.illustrationPreview}
                alt="preview"
                height={120}
                width={170}
                unoptimized
              />
              <div className={styles.illustrationSource}>
                {i.source.title}
              </div>
              <div className={styles.illustrationName}>{i.title}</div>
              <div className={styles.illustrationAction}>
                Use Image →
              </div>
            </div>
          )))}
        </div>
      </div>
    </div>
  );
}
